import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { CameraPlaceholder } from './CameraPlaceholder';
import { ContentArea } from './ContentArea';
import { TopBar, NextSectionIndicator } from './TopBar';
import { BottomBar } from './BottomBar';
import { IntroView } from '../Intro/IntroView';
import { OutroView } from '../Outro/OutroView';
import { useEditorStore } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { ElementToolbar } from '../Canvas/ElementToolbar';
import { SaveStatusIndicator } from '../Editor/SaveStatusIndicator';

import { useAutoSave } from '../../hooks/useAutoSave';
import { updateEpisode } from '../../api/client';

interface MainLayoutProps {
  isPresentation?: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ isPresentation = false }) => {
  const { cameraSize, setCameraSize, isFullscreen, currentEpisode, currentSectionIndex, setCurrentSection, viewMode, setViewMode, selectedElementId, updateElement, removeElement, addSection, removeSection, canRemoveSection, updateSectionById } = useEditorStore();
  const { settings } = useSettingsStore();
  const [isDragging, setIsDragging] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Keyboard shortcuts (Delete, Ctrl+D)
  useKeyboardShortcuts();

  // Simple autosave - just save sections as JSON blob
  const handleSave = useCallback(async (sections: Parameters<typeof useAutoSave>[0]['sections']) => {
    if (!currentEpisode) return;
    await updateEpisode(currentEpisode.id, { sections: sections as unknown[] });
  }, [currentEpisode]);

  const { saveStatus } = useAutoSave({
    sections: currentEpisode?.sections || [],
    onSave: handleSave,
    debounceMs: 2000,
  });

  const isEditMode = !isPresentation;

  // Handle camera size dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
      setCameraSize(Math.max(15, Math.min(35, percentage)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, setCameraSize]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Presentation mode: fixed 2560x1440 layout
  if (!isEditMode) {
    const sections = currentEpisode?.sections || [];
    
    // Render content based on viewMode
    const renderContent = () => {
      if (viewMode === 'intro') return <IntroView isPresentation />;
      if (viewMode === 'outro') return <OutroView isPresentation />;
      return (
        <div className="w-full h-full flex items-center justify-center relative">
          <div className="w-full flex flex-col py-2 gap-2" style={{ height: '80%' }}>
            <TopBar />
            <div className="flex-1 relative min-h-0">
              <ContentArea isPresentation />
            </div>
            <BottomBar />
          </div>
          {/* Floating next section indicator */}
          <div className="absolute bottom-8 right-4">
            <NextSectionIndicator />
          </div>
        </div>
      );
    };
    
    return (
      <div className="w-full h-full bg-black flex overflow-auto">
        {/* Fixed 2560x1440 Container */}
        <div 
          ref={containerRef}
          className="relative bg-black flex-shrink-0"
          style={{ width: 2560, height: 1440 }}
        >
          {/* Camera zone - always visible */}
          <div
            className="absolute left-0 top-0 bottom-0 z-10 p-2"
            style={{ width: `${cameraSize}%` }}
          >
            <CameraPlaceholder />
          </div>
          
          {/* Draggable edge for resizing camera */}
          <div
            onMouseDown={handleResizeStart}
            className="absolute top-0 bottom-0 w-2 z-20 cursor-col-resize hover:bg-white/10"
            style={{ left: `calc(${cameraSize}% - 4px)` }}
          />

          {/* Content zone - always visible, content changes based on viewMode */}
          <div
            className="absolute top-0 bottom-0 flex flex-col"
            style={{
              left: `${cameraSize}%`,
              right: '32px',
            }}
          >
            {renderContent()}
          </div>
        </div>

        {/* Controls Panel - right side */}
        <div className="flex-1 flex flex-col justify-start items-start px-6 py-8 gap-2 max-w-md">
          {/* Navigation */}
          <div className="flex flex-col gap-2 mb-4">
            {/* Intro */}
            <button
              onClick={() => setViewMode('intro')}
              className={`text-sm ${
                viewMode === 'intro'
                  ? 'text-purple-400'
                  : 'text-gray-600 hover:text-gray-500'
              }`}
            >
              Intro
            </button>
            
            <div className="border-t border-gray-800 w-8 my-1" />
            
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => { setCurrentSection(index); setViewMode('section'); }}
                className={`text-sm text-left ${
                  index === currentSectionIndex && viewMode === 'section'
                    ? 'text-orange-400'
                    : 'text-gray-600 hover:text-gray-500'
                }`}
              >
                {section.title || `Section ${index + 1}`}
              </button>
            ))}
            
            <div className="border-t border-gray-800 w-8 my-1" />
            
            {/* Outro */}
            <button
              onClick={() => setViewMode('outro')}
              className={`text-sm ${
                viewMode === 'outro'
                  ? 'text-purple-400'
                  : 'text-gray-600 hover:text-gray-500'
              }`}
            >
              Outro
            </button>
          </div>

          {/* Talking Points - visible when section is selected */}
          {viewMode === 'section' && sections[currentSectionIndex]?.talkingPoints && (
            <div className="flex-1 w-full mt-4 p-4 bg-gray-900/50 rounded-lg border border-gray-800">
              <h3 className="text-gray-400 text-xs font-medium mb-2 uppercase tracking-wide">Notes</h3>
              <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">
                {sections[currentSectionIndex].talkingPoints}
              </p>
            </div>
          )}
          
          <div className="mt-auto pt-4 border-t border-gray-800">
            <button
              onClick={() => {
                if (document.fullscreenElement) {
                  document.exitFullscreen?.();
                }
                navigate(`/episodes/${currentEpisode?.id}`);
              }}
              className="text-sm text-gray-600 hover:text-gray-500"
            >
              Retour à l'éditeur
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fixed canvas dimensions (presentation resolution)
  const CANVAS_WIDTH = 2560;
  const CANVAS_HEIGHT = 1440;

  // Crop camera zone in editor: only fit the content area (right of camera)
  const CAMERA_OFFSET = CANVAS_WIDTH * (cameraSize / 100);
  const CONTENT_ONLY_WIDTH = CANVAS_WIDTH - CAMERA_OFFSET;
  const CROPPED_ASPECT = CONTENT_ONLY_WIDTH / CANVAS_HEIGHT;

  // Sidebar dimensions
  const SIDEBAR_WIDTH = 256; // w-64
  const SIDEBAR_COLLAPSED_WIDTH = 48;

  // Calculate display size to fit available space while maintaining cropped aspect ratio
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1280, height: 720, scale: 0.5 });

  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return;

    const updateSize = () => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth === 0 || clientHeight === 0) return;
      
      // Fit only the content area (camera zone is cropped)
      const padding = 32; // 16px on each side
      const availableWidth = clientWidth - padding;
      const availableHeight = clientHeight - padding;
      
      let width, height;
      if (availableWidth / availableHeight > CROPPED_ASPECT) {
        // Container is wider than cropped aspect, constrain by height
        height = availableHeight;
        width = height * CROPPED_ASPECT;
      } else {
        // Container is taller than cropped aspect, constrain by width
        width = availableWidth;
        height = width / CROPPED_ASPECT;
      }
      
      const scale = width / CONTENT_ONLY_WIDTH;
      setCanvasSize({ width, height, scale });
    };

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);
    updateSize(); // Initial calculation

    return () => resizeObserver.disconnect();
  }, [cameraSize, CROPPED_ASPECT, CONTENT_ONLY_WIDTH]);

  // Edit mode layout - fixed 2560x1440 canvas scaled to fit
  const renderEditContent = () => {
    if (viewMode === 'intro') return <IntroView />;
    if (viewMode === 'outro') return <OutroView />;
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <div className="w-full flex flex-col py-2 gap-2" style={{ height: '80%' }}>
          <TopBar />
          <div className="flex-1 relative min-h-0">
            <ContentArea scale={canvasSize.scale} />
          </div>
          <BottomBar />
        </div>
        {/* Floating next section indicator */}
        <div className="absolute bottom-8 right-4">
          <NextSectionIndicator />
        </div>
      </div>
    );
  };

  // Get selected element for toolbar
  const currentSection = currentEpisode?.sections[currentSectionIndex];
  const selectedElement = currentSection?.elements.find(el => el.id === selectedElementId);

  return (
    <div className={`relative w-full h-full overflow-hidden bg-gray-950 ${isFullscreen ? 'bg-black' : ''}`}>
      {/* Element Toolbar - fixed at top, accounts for left sidebar */}
      {selectedElement && viewMode === 'section' && (
        <div 
          className="fixed top-0 right-0 z-40"
          style={{ left: SIDEBAR_COLLAPSED_WIDTH }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <ElementToolbar
            element={selectedElement}
            onUpdate={(changes) => updateElement(selectedElement.id, changes)}
            onDelete={() => removeElement(selectedElement.id)}
          />
        </div>
      )}

      {/* Left Sidebar - single layout, overflow:hidden clips to icon column when collapsed */}
      <div
        className="fixed left-0 top-0 bottom-0 z-50 bg-gray-900 border-r border-gray-700 overflow-hidden transition-[width] duration-200 ease-in-out"
        style={{ width: sidebarHovered ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex flex-col h-full" style={{ width: SIDEBAR_WIDTH }}>
          {/* Back button - moved to top */}
          <div className="py-2 border-b border-gray-700">
            <button
              onClick={() => navigate('/episodes')}
              className="w-full flex items-center px-1 py-0.5 group"
            >
              <div className="w-10 h-8 shrink-0 rounded-lg flex items-center justify-center text-gray-400 group-hover:bg-gray-700 group-hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
              <span className="ml-2 text-sm text-gray-300 whitespace-nowrap">Retour</span>
            </button>
          </div>

          {/* Scrollable section list */}
          <div className="flex-1 overflow-y-auto py-2 space-y-0.5 min-h-0">
            {/* Intro */}
            <button
              onClick={() => navigate(`/episodes/${currentEpisode?.id}/intro`)}
              className={`w-full flex items-center px-1 py-0.5 group`}
            >
              <div className={`w-10 h-8 shrink-0 rounded-lg flex items-center justify-center text-sm transition-colors ${
                viewMode === 'intro' ? 'bg-purple-600 text-white' : 'text-gray-400 group-hover:bg-gray-800 group-hover:text-white'
              }`}>
                🎬
              </div>
              <span className={`ml-2 text-sm font-medium whitespace-nowrap ${
                viewMode === 'intro' ? 'text-purple-300' : 'text-gray-300'
              }`}>Intro</span>
            </button>

            <div className="mx-3 border-t border-gray-700 my-1" />

            {/* Sections */}
            {(currentEpisode?.sections || []).map((section, index) => {
              const isActive = index === currentSectionIndex && viewMode === 'section';
              const contentIndex = (currentEpisode?.sections || []).slice(0, index).filter(s => s.type === 'content').length + 1;
              const canDelete = section.type === 'content' && canRemoveSection(index);
              return (
                <div key={section.id} className="flex items-center px-1 py-0.5 group">
                  <button
                    onClick={() => navigate(`/episodes/${currentEpisode?.id}/section/${index}`)}
                    className="flex items-center flex-1 min-w-0"
                  >
                    <div className={`w-10 h-8 shrink-0 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${
                      isActive ? 'bg-orange-600 text-white' : 'text-gray-400 group-hover:bg-gray-800 group-hover:text-white'
                    }`}>
                      {contentIndex}
                    </div>
                    <span className={`ml-2 text-sm truncate whitespace-nowrap ${
                      isActive ? 'text-orange-300 font-medium' : 'text-gray-300'
                    }`}>
                      {section.title || `Section ${contentIndex}`}
                    </span>
                  </button>
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSectionToDelete(index); }}
                      className={`shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                        isActive ? 'text-orange-200 hover:text-white hover:bg-white/20' : 'text-red-400 hover:text-red-300 hover:bg-red-900/30'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add section */}
            <button
              onClick={() => addSection()}
              className="w-full flex items-center px-1 py-0.5 group"
            >
              <div className="w-10 h-8 shrink-0 rounded-lg flex items-center justify-center text-sm text-gray-500 group-hover:text-orange-400 border border-dashed border-gray-600 group-hover:border-orange-500 group-hover:bg-orange-500/10 transition-colors">
                +
              </div>
              <span className="ml-2 text-sm text-gray-400 group-hover:text-orange-400 whitespace-nowrap transition-colors">Ajouter une section</span>
            </button>

            <div className="mx-3 border-t border-gray-700 my-1" />

            {/* Outro */}
            <button
              onClick={() => navigate(`/episodes/${currentEpisode?.id}/outro`)}
              className={`w-full flex items-center px-1 py-0.5 group`}
            >
              <div className={`w-10 h-8 shrink-0 rounded-lg flex items-center justify-center text-sm transition-colors ${
                viewMode === 'outro' ? 'bg-purple-600 text-white' : 'text-gray-400 group-hover:bg-gray-800 group-hover:text-white'
              }`}>
                🎬
              </div>
              <span className={`ml-2 text-sm font-medium whitespace-nowrap ${
                viewMode === 'outro' ? 'text-purple-300' : 'text-gray-300'
              }`}>Outro</span>
            </button>

            {/* Save status */}
            {saveStatus && saveStatus !== 'idle' && (
              <div className="px-3 mt-2">
                <SaveStatusIndicator status={saveStatus} />
              </div>
            )}
          </div>

          {/* Talking points - expanded only, when section selected */}
          <div
            className="overflow-hidden transition-all duration-200 border-t border-gray-700"
            style={{
              maxHeight: sidebarHovered && viewMode === 'section' && currentEpisode?.sections[currentSectionIndex] ? '200px' : '0px',
              opacity: sidebarHovered && viewMode === 'section' ? 1 : 0,
              borderTopWidth: sidebarHovered && viewMode === 'section' && currentEpisode?.sections[currentSectionIndex] ? undefined : '0px',
            }}
          >
            <div className="p-3">
              <label className="block text-gray-400 text-xs font-medium mb-1">Notes</label>
              <textarea
                value={currentEpisode?.sections[currentSectionIndex]?.talkingPoints || ''}
                onChange={(e) => {
                  const sec = currentEpisode?.sections[currentSectionIndex];
                  if (sec) updateSectionById(sec.id, { talkingPoints: e.target.value });
                }}
                placeholder="Notes..."
                className="w-full h-24 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-xs resize-none focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>

          {/* Bottom section */}
          <div className="border-t border-gray-700 py-2 space-y-0.5">
            {/* AI Prompt buttons - expanded only */}
            <div
              className="overflow-hidden transition-all duration-200"
              style={{
                maxHeight: sidebarHovered ? '200px' : '0px',
                opacity: sidebarHovered ? 1 : 0,
              }}
            >
              <div className="px-3 pb-2 space-y-1.5 border-b border-gray-600 mb-2">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Prompts IA</p>
                {([
                  { type: 'title' as const, label: 'Titre', icon: '✨', classes: 'bg-blue-600/20 hover:bg-blue-600/30 border-blue-500/50 text-blue-300' },
                  { type: 'description' as const, label: 'Description', icon: '📝', classes: 'bg-green-600/20 hover:bg-green-600/30 border-green-500/50 text-green-300' },
                  { type: 'thumbnail' as const, label: 'Miniature', icon: '🖼️', classes: 'bg-purple-600/20 hover:bg-purple-600/30 border-purple-500/50 text-purple-300' },
                ]).map(({ type, label, icon, classes }) => (
                    <button
                      key={type}
                      onClick={async () => {
                        if (!currentEpisode) return;
                        const showName = currentEpisode.showTitle || settings.showName;
                        const contexts = currentEpisode.sections.map((s, i) => {
                          const els = s.elements.filter(e => e.type === 'text' || e.type === 'title-block').map(e => e.content).filter(Boolean).join(' | ');
                          return `**${i + 1}. ${s.title || `Section ${i + 1}`}**${els ? `\n  Éléments: ${els.substring(0, 200)}` : ''}${s.talkingPoints ? `\n  Notes: ${s.talkingPoints.substring(0, 300)}` : ''}`;
                        }).join('\n\n');
                        const prompt = type === 'title'
                          ? `Génère un titre pour une vidéo YouTube de "${showName}".\n\nCONTENU :\n${contexts}\n\nMax 80 caractères, style direct.`
                          : type === 'description'
                          ? `Rédige une description SEO pour une vidéo YouTube de "${showName}".\n\nCONTENU :\n${contexts}\n\n200-300 mots, hashtags inclus.`
                          : `Create a professional YouTube thumbnail for "${showName}".\n\nCONTENT:\n${contexts}\n\n16:9, 4K, dramatic lighting.`;
                        await navigator.clipboard.writeText(prompt);
                        setCopiedPrompt(type);
                        setTimeout(() => setCopiedPrompt(null), 1500);
                      }}
                      className={`w-full px-2 py-1.5 border rounded-lg font-medium text-xs flex items-center justify-center gap-1.5 transition-all ${classes}`}
                    >
                      <span>{copiedPrompt === type ? '✓' : icon}</span>
                      <span>{copiedPrompt === type ? 'Copié !' : `Prompt ${label.toLowerCase()}`}</span>
                    </button>
                  ))}
              </div>
            </div>

            {/* Presentation mode */}
            <button
              onClick={() => {
                document.documentElement.requestFullscreen?.();
                navigate(`/episodes/${currentEpisode?.id}/present`);
              }}
              className="w-full flex items-center px-1 py-0.5 group"
            >
              <div className="w-10 h-8 shrink-0 rounded-lg flex items-center justify-center bg-orange-600/20 text-orange-400 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="ml-2 text-sm text-orange-300 font-medium whitespace-nowrap">Présentation</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete section confirmation modal */}
      {sectionToDelete !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]" onClick={() => setSectionToDelete(null)}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm mx-4 border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Confirmer la suppression</h3>
            <p className="text-gray-300 mb-4">
              Voulez-vous vraiment supprimer la section "{currentEpisode?.sections[sectionToDelete]?.title || 'sans titre'}" ?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setSectionToDelete(null)} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded">Annuler</button>
              <button onClick={() => { removeSection(sectionToDelete); setSectionToDelete(null); }} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-full pt-10" style={{ paddingLeft: SIDEBAR_COLLAPSED_WIDTH }}>
        {/* Main Content - Camera zone cropped, content area maximized */}
        <div 
          ref={editorContainerRef}
          className="flex-1 flex items-center justify-center overflow-hidden"
        >
          <div 
            className="relative bg-black rounded-lg overflow-hidden shadow-2xl"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
            }}
          >
            {/* Inner container at full 2560x1440, shifted left to crop camera zone */}
            <div
              ref={containerRef}
              className="absolute origin-top-left"
              style={{
                width: CANVAS_WIDTH,
                height: CANVAS_HEIGHT,
                transform: `scale(${canvasSize.scale})`,
                left: -CAMERA_OFFSET * canvasSize.scale,
              }}
            >
              {/* Camera zone - cropped off-screen by shell overflow:hidden */}
              <div
                className="absolute left-0 top-0 bottom-0 z-10"
                style={{ width: `${cameraSize}%` }}
              />

              {/* Content zone */}
              <div
                className="absolute top-0 bottom-0 flex flex-col"
                style={{
                  left: `${cameraSize}%`,
                  right: '32px',
                }}
              >
                {renderEditContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
